import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X } from "lucide-react";

const STORAGE_KEY = "reallyfan_install_dismissed_at";
const DISMISS_DAYS = 7;

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if user dismissed recently
    const dismissedAt = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const days = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }

    // Don't show if already installed (running in standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Delay before showing to avoid being intrusive
      setTimeout(() => setVisible(true), 8000);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari doesn't fire beforeinstallprompt — show a custom hint
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setTimeout(() => setVisible(true), 10000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setDeferredPrompt(null);
      }
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-20 left-3 right-3 z-50 bg-card border border-border rounded-3xl p-4 shadow-2xl flex items-start gap-3 md:left-auto md:right-4 md:max-w-sm"
          data-testid="install-prompt"
        >
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-card-foreground">Installer ReallyFan</p>
            {isIOS ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Appuie sur <span className="font-bold">Partager</span> puis <span className="font-bold">Sur l'ecran d'accueil</span>
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Acces rapide depuis ton ecran d'accueil, mode plein ecran et notifications.
              </p>
            )}
            {!isIOS && deferredPrompt && (
              <button
                onClick={handleInstall}
                className="mt-2 bg-[#FF6B35] text-white font-bold px-3 py-1.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform"
                data-testid="install-prompt-btn"
              >
                Installer
              </button>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="install-prompt-dismiss"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
